import { useState, useCallback, useEffect, useRef } from "react";

export function useQuery<T>(apiCall: () => Promise<T>, deps: React.DependencyList = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const generation = useRef(0);
  const pendingRefetches = useRef(0);

  useEffect(() => {
    generation.current += 1;
    pendingRefetches.current = 0;
    const requestGeneration = generation.current;

    const runFetch = async () => {
      try {
        setLoading(true);
        setIsRefetching(false);
        setError(null);
        const result = await apiCall();
        if (requestGeneration === generation.current) {
          setData(result);
        }
      } catch (err) {
        if (requestGeneration === generation.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (requestGeneration === generation.current) {
          setLoading(false);
        }
      }
    };

    runFetch();

    return () => {
      generation.current += 1;
    };
  }, deps);

  const fetchData = useCallback(async () => {
    const requestGeneration = generation.current;
    pendingRefetches.current += 1;

    try {
      setIsRefetching(true);
      setError(null);
      const result = await apiCall();
      if (requestGeneration === generation.current) {
        setData(result);
      }
    } catch (err) {
      if (requestGeneration === generation.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (requestGeneration === generation.current) {
        pendingRefetches.current -= 1;
        setLoading(false);
        if (pendingRefetches.current === 0) {
          setIsRefetching(false);
        }
      }
    }
  }, deps);

  return { data, loading, error, refetch: fetchData, isRefetching };
}

export function useMutation<T, Args extends unknown[] = []>(
  apiCall: (...args: Args) => Promise<T>,
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (...args: Args) => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiCall(...args);
        setData(result);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [apiCall],
  );

  const reset = () => {
    setData(null);
    setError(null);
    setLoading(false);
  };

  return { data, loading, error, mutate, reset };
}
